import React, { useState } from "react";
import TaskListEditor from "../editors/TaskListEditor";
import TaskPickerModal from "../pickers/TaskPickerModal";

/**
 * Reusable TaskSet form component
 * Used in both Registry and Project contexts
 * Includes nested TaskListEditor
 */
const TaskSetForm = ({
  value,
  onChange,
  mode = "project",
  showSaveToRegistry = false,
  disabled = false
}) => {
  const [showTaskPicker, setShowTaskPicker] = useState(false);

  const handleChange = (field, val) => {
    onChange({ ...value, [field]: val });
  };

  const handleTasksChange = (tasks) => {
    handleChange("tasks", tasks);
  };

  const handleAddTasksFromRegistry = (selectedTasks) => {
    const newTasks = selectedTasks.map((task, index) => ({
      id: task._id,
      _id: task._id,
      name: task.name,
      description: task.description || "",
      weight: task.weight || task.defaultWeight || 1,
      estimatedHours: task.estimatedHours || task.defaultEstimatedHours || "",
      tags: task.tags || [],
      registryId: task._id,
      sortOrder: (value.tasks?.length || 0) + index,
      // Convert subtaskRefs to subTasks array
      subTasks: (task.subtaskRefs || []).map((ref, subIndex) => {
        const subtask = typeof ref.subtaskId === 'object' ? ref.subtaskId : { _id: ref.subtaskId };
        return {
          id: subtask._id,
          _id: subtask._id,
          name: subtask.name || "",
          description: subtask.description || "",
          weight: ref.weight || subtask.weight || subtask.defaultWeight || 1,
          estimatedHours: subtask.estimatedHours || subtask.defaultEstimatedHours || "",
          registryId: subtask._id,
          sortOrder: subIndex
        };
      })
    }));

    handleChange("tasks", [...(value.tasks || []), ...newTasks]);
  };

  const existingTaskIds = (value.tasks || [])
    .map(t => t.registryId || t._id)
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Name *</label>
        <input
          type="text"
          value={value.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          placeholder="e.g., Pool Construction"
          disabled={disabled}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          value={value.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          rows={3}
          placeholder="Optional description..."
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
          <input
            type="text"
            value={Array.isArray(value.tags) ? value.tags.join(", ") : (value.tags || "")}
            onChange={(e) => handleChange("tags", e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="e.g., pool, construction, standard"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Est. Hours</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={value.estimatedHours || ""}
            onChange={(e) => handleChange("estimatedHours", e.target.value ? parseFloat(e.target.value) : "")}
            className="w-full px-3 py-2 border rounded"
            placeholder="40.0"
            disabled={disabled}
          />
        </div>
      </div>

      {showSaveToRegistry && mode === "project" && (
        <div className="border-t pt-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={value.saveToRegistry || false}
              onChange={(e) => handleChange("saveToRegistry", e.target.checked)}
              className="mt-1 mr-2"
              disabled={disabled}
            />
            <div>
              <div className="font-medium text-sm">Save to Registry</div>
              <div className="text-xs text-gray-600">
                Make this task set reusable for future projects
              </div>
            </div>
          </label>
        </div>
      )}

      {/* Tasks Section */}
      <div className="border-t pt-4">
        <TaskListEditor
          tasks={value.tasks || []}
          onChange={handleTasksChange}
          mode={mode}
          showSaveToRegistry={showSaveToRegistry}
          onAddFromRegistry={() => setShowTaskPicker(true)}
          disabled={disabled}
        />
      </div>

      {/* Task Picker Modal */}
      <TaskPickerModal
        isOpen={showTaskPicker}
        onClose={() => setShowTaskPicker(false)}
        onSelect={handleAddTasksFromRegistry}
        existingIds={existingTaskIds}
      />
    </div>
  );
};

export default TaskSetForm;
